/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { DomController } from './DomController';
import type { NoteService } from './NoteService';
import type { SettingsService } from './SettingsService';
import type { EditorService } from './EditorService';
import type { GeminiService } from './GeminiService';
import type { DBService } from './DBService';
import type { Note, TokenUsageRecord, UsageMetadata } from '../types/types';
import type { AudioService } from './AudioService';
import type { DialogService } from './DialogService';

/**
 * Handles general UI interactions and state, such as popovers and clipboard actions.
 */
export class UIService {
  private statusManager: StatusManager;
  
  // Service registry getters
  private get dom() { return ServiceRegistry.get<DomController>('dom'); }
  private get settingsService() { return ServiceRegistry.get<SettingsService>('settingsService'); }
  private get geminiService() { return ServiceRegistry.get<GeminiService>('gemini'); }
  private get dbService() { return ServiceRegistry.get<DBService>('dbService'); }
  private get noteService() { return ServiceRegistry.get<NoteService>('noteService'); }
  private get editorService() { return ServiceRegistry.get<EditorService>('editorService'); }
  private get audioService() { return ServiceRegistry.get<AudioService>('audioService'); }
  private get dialogService() { return ServiceRegistry.get<DialogService>('dialogService'); }
  
  private _debouncedSaveSettings: () => void;
  private bypassContextMenu = false;
  private lastContextMenuEvent: MouseEvent | null = null;
  private isSideBySideViewActive = false;
  private isFocusModeActive = false;
  private activeTabBeforeSbs: HTMLButtonElement | null = null;
  
  constructor() {
    this.statusManager = new StatusManager(this.dom);
    this._debouncedSaveSettings = this._debounce(this._saveSettingsToDrive.bind(this), 2000);
    this.bindEventListeners();
  }

  private bindEventListeners(): void {
    const settingsChangeHandler = async (updateFunction: () => Promise<void>) => {
        await updateFunction.call(this.settingsService);
        this._debouncedSaveSettings();
    };
    
    // Header & Title Tools
    this.dom.settingsButton.addEventListener('click', (e) => this.toggleSettingsPopover(e));
    this.dom.toggleFormattingToolbarButton.addEventListener('click', () => this.toggleToolbarView('format'));
    this.dom.toggleFindReplaceButton.addEventListener('click', () => this.toggleToolbarView('find'));
    this.dom.sideBySideButton.addEventListener('click', () => this.toggleSideBySideView());
    this.dom.focusModeButton.addEventListener('click', () => this.toggleFocusMode());
    this.dom.exitFocusModeButton.addEventListener('click', () => this.toggleFocusMode());
    this.dom.statsButton.addEventListener('click', () => this.toggleStatsModal());
    this.dom.tokenUsageButton.addEventListener('click', () => this.toggleTokenUsageModal());
    this.dom.toggleCommandBarButton.addEventListener('click', () => this.toggleCommandBar());
    
    // Settings Popover
    this.dom.modelSelect.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateModelSelection));
    this.dom.modelCustomInput.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateModelSelection));
    this.dom.voiceTranscriptionModelSelect.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateVoiceTranscriptionModelSelection));
    this.dom.voiceTranscriptionModelCustomInput.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateVoiceTranscriptionModelSelection));
    this.dom.fastModelSelect.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateFastModelSelection));
    this.dom.fastModelCustomInput.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateFastModelSelection));
    this.dom.titleGeneratorModelSelect.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateTitleGeneratorModelSelection));
    this.dom.titleGeneratorModelCustomInput.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateTitleGeneratorModelSelection));
    this.dom.rewritePolishingModelSelect.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateRewritePolishingModelSelection));
    this.dom.rewritePolishingModelCustomInput.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateRewritePolishingModelSelection));
    this.dom.themeToggleSwitch.addEventListener('change', async () => {
        const newTheme = this.dom.themeToggleSwitch.checked ? 'light' : 'dark';
        document.documentElement.dataset.theme = newTheme;
        await settingsChangeHandler(() => this.settingsService.setTheme(newTheme));
    });
    
    this.dom.rewriteButton.addEventListener('click', (e) => this.toggleRewritePopover(e));
    this.dom.polishToggle.addEventListener('change', () => settingsChangeHandler(this.settingsService.updatePolishingState));
    
    this.dom.saveToDriveButton.addEventListener('click', (e) => this.toggleDrivePopover(e));
    const saveOptionToggles = [this.dom.savePolishedToggle, this.dom.saveRawToggle, this.dom.saveTranslatedToggle];
    saveOptionToggles.forEach(toggle => {
        toggle.addEventListener('change', () => this.updateConfirmSaveButtonState());
    });


    this.dom.translateButton.addEventListener('click', (e) => this.toggleTranslatePopover(e));
    this.dom.translateToggle.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateTranslationState));
    this.dom.translatePolishedToggle.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateTranslationState));
    this.dom.translateRawToggle.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateTranslationState));
    this.dom.translateLanguageSelect.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateTranslationState));
    this.dom.outputLanguageSelect.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateOutputLanguage));

    // Autosave settings
    this.dom.autosaveToggle.addEventListener('change', () => this.handleAutosaveSettingChange());
    this.dom.autosaveFrequency.addEventListener('change', () => this.handleAutosaveSettingChange());
    this.dom.recordingModeToggle.addEventListener('change', () => settingsChangeHandler(this.settingsService.updateRecordingMode));
    
    // Find & Replace
    this.dom.findNextButton.addEventListener('click', () => this.editorService.findAndReplace('next'));
    this.dom.findPrevButton.addEventListener('click', () => this.editorService.findAndReplace('prev'));
    this.dom.replaceButton.addEventListener('click', () => this.editorService.findAndReplace('replace'));
    this.dom.replaceAllButton.addEventListener('click', () => this.editorService.findAndReplace('replaceAll'));

    // Stats Dialog
    this.dom.closeStatsDialogButton.addEventListener('click', () => this.toggleStatsModal());
    this.dom.statsOverlay.addEventListener('click', (e) => {
        if (e.target === this.dom.statsOverlay) this.toggleStatsModal();
    });
    
    // Token Usage Dialog
    this.dom.closeTokenUsageDialogButton.addEventListener('click', () => this.toggleTokenUsageModal());
    this.dom.exportTokenUsageButton.addEventListener('click', () => this.exportTokenUsageToCSV());
    this.dom.tokenUsageOverlay.addEventListener('click', (e) => {
        if (e.target === this.dom.tokenUsageOverlay) this.toggleTokenUsageModal();
    });
    
    // Context Menu
    const noteContentWrapper = document.querySelector('.note-content-wrapper');
    noteContentWrapper?.addEventListener('contextmenu', (e) => {
      if (this.bypassContextMenu) {
        this.bypassContextMenu = false;
        return;
      }
      this.showContextMenu(e as MouseEvent);
    });

    document.addEventListener('click', (e) => this.handleGlobalClick(e));
    window.addEventListener('resize', this.handleResize.bind(this));
    
    this.dom.stickyToolbarToggle.addEventListener('click', async () => {
      await settingsChangeHandler(() => this.settingsService.updateToolbarStickyState(!this.settingsService.isToolbarSticky));
      this.updateToolbarStickyVisuals();
    });
  }

  public initInitialUIState(): void {
    this.updateToolbarStickyVisuals();
    this.updateToolbarButtonStates();
    this.setInitialCommandBarState();
  }

  private updateToolbarStickyVisuals(): void {
    this.dom.editorToolbarContainer.classList.toggle('sticky', this.settingsService.isToolbarSticky);
    this.dom.stickyToolbarToggle.classList.toggle('active', this.settingsService.isToolbarSticky);
  }

  private toggleSettingsPopover(event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = !this.dom.settingsPopover.classList.contains('hidden');
    this.closeAllPopovers();
    if (!wasOpen) {
      this.dom.settingsPopover.classList.remove('hidden');
    }
  }

  private toggleRewritePopover(event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = !this.dom.rewritePopover.classList.contains('hidden');
    this.closeAllPopovers();
    if (!wasOpen) {
      this.dom.rewritePopover.classList.remove('hidden');
    }
  }

  private toggleDrivePopover(event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = !this.dom.drivePopover.classList.contains('hidden');
    this.closeAllPopovers();

    if (!wasOpen) {
        if (this.noteService.currentNote && this.noteService.currentNote.translatedNote.trim()) {
            this.dom.saveTranslatedOption.classList.remove('hidden');
        } else {
            this.dom.saveTranslatedOption.classList.add('hidden');
            this.dom.saveTranslatedToggle.checked = false;
        }

        // Populate metadata fields with current note's data
        if (this.noteService.currentNote) {
            this.dom.driveCategoryInput.value = this.noteService.currentNote.category || '';
            this.dom.driveTagsInput.value = this.noteService.currentNote.tags ? this.noteService.currentNote.tags.join(', ') : '';
        }

        this.dom.drivePopover.classList.remove('hidden');
        this.updateConfirmSaveButtonState();
    }
  }

  private toggleTranslatePopover(event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = !this.dom.translatePopover.classList.contains('hidden');
    this.closeAllPopovers();
    if (!wasOpen) {
      this.dom.translatePopover.classList.remove('hidden');
    }
  }
  
  public closeAllPopovers(): void {
    this.dom.drivePopover.classList.add('hidden');
    this.dom.translatePopover.classList.add('hidden');
    this.dom.rewritePopover.classList.add('hidden');
    this.dom.settingsPopover.classList.add('hidden');
  }

  private handleGlobalClick(event: MouseEvent): void {
    const target = event.target as Element;
    if (!target.closest('.drive-popover') && !target.closest('.drive-button-container') && !target.closest('.action-button-container') && !target.closest('.header-action-button')) {
        this.closeAllPopovers();
    }
    if (!target.closest('.custom-context-menu')) {
        this.dom.customContextMenu.classList.add('hidden');
    }
  }

  private handleResize(): void {
    if ((this.dom.recordingInterface as any).isRecording) requestAnimationFrame(() => this.dom.setupCanvasDimensions());
    requestAnimationFrame(() => {
        const currentActiveButton = document.querySelector(".tab-navigation .tab-button.active") as HTMLButtonElement;
        if (currentActiveButton) this.dom.setActiveTab(currentActiveButton, true);
    });
  }

  private async handleAutosaveSettingChange(): Promise<void> {
      await this.settingsService.updateAutosaveState();
      // The NoteService is now responsible for restarting the timer.
      // This event will bubble up to the AppController which can coordinate.
      // For now, we rely on the user having a NoteService instance to call restart on.
  }

  private _debounce(func: Function, delay: number) {
    let timeout: number;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = window.setTimeout(() => func.apply(this, args), delay);
    };
  }
  
  private async _saveSettingsToDrive() {
      // This is now handled by the AuthService
  }

  private updateToolbarButtonStates(): void {
    const isToolbarVisible = !this.dom.markdownToolbar.classList.contains('hidden');
    const isFormatVisible = !this.dom.formattingControls.classList.contains('hidden');
    const isFindVisible = !this.dom.findReplaceControls.classList.contains('hidden');
    
    this.dom.toggleFormattingToolbarButton.classList.toggle('active', isToolbarVisible && isFormatVisible);
    this.dom.toggleFindReplaceButton.classList.toggle('active', isToolbarVisible && isFindVisible);
  }

  public toggleToolbarView(view: 'format' | 'find'): void {
    const isToolbarVisible = !this.dom.markdownToolbar.classList.contains('hidden');
    const isFormatCurrentlyVisible = !this.dom.formattingControls.classList.contains('hidden');
    const isFindCurrentlyVisible = !this.dom.findReplaceControls.classList.contains('hidden');

    const wantsFormat = view === 'format';

    if (!isToolbarVisible) {
        this.dom.markdownToolbar.classList.remove('hidden');
        if (wantsFormat) {
            this.dom.formattingControls.classList.remove('hidden');
            this.dom.findReplaceControls.classList.add('hidden');
        } else {
            this.dom.formattingControls.classList.add('hidden');
            this.dom.findReplaceControls.classList.remove('hidden');
            this.dom.findInput.focus();
            this.dom.findInput.select();
        }
    } else {
        const isCorrectViewVisible = (wantsFormat && isFormatCurrentlyVisible) || (!wantsFormat && isFindCurrentlyVisible);
        if (isCorrectViewVisible) {
            this.dom.markdownToolbar.classList.add('hidden');
        } else {
            if (wantsFormat) {
                this.dom.formattingControls.classList.remove('hidden');
                this.dom.findReplaceControls.classList.add('hidden');
            } else {
                this.dom.formattingControls.classList.add('hidden');
                this.dom.findReplaceControls.classList.remove('hidden');
                this.dom.findInput.focus();
                this.dom.findInput.select();
            }
        }
    }
    
    this.updateToolbarButtonStates();
  }
  
  private showContextMenu(event: MouseEvent): void {
      event.preventDefault();
      this.dom.customContextMenu.classList.add('hidden'); // Hide first to recalculate position
  
      const target = event.target as HTMLElement;
      const isEditable = target.closest('#polishedNote') || target.closest('#translatedNote');
  
      if (!isEditable) {
          return;
      }
  
      this.lastContextMenuEvent = event;
      const selection = window.getSelection();
      const isCollapsed = !selection || selection.isCollapsed;
  
      this._populateContextMenu(isCollapsed, !!selection?.toString().trim());
  
      // Positioning logic
      const { clientX: mouseX, clientY: mouseY } = event;
      const { innerWidth: winWidth, innerHeight: winHeight } = window;
      const menu = this.dom.customContextMenu;
      
      // Temporarily show to measure
      menu.style.visibility = 'hidden';
      menu.classList.remove('hidden');
      const menuWidth = menu.offsetWidth;
      const menuHeight = menu.offsetHeight;
      menu.classList.add('hidden');
      menu.style.visibility = '';
  
      let x = mouseX;
      let y = mouseY;
  
      if (mouseX + menuWidth > winWidth) {
          x = winWidth - menuWidth - 10;
      }
      if (mouseY + menuHeight > winHeight) {
          y = winHeight - menuHeight - 10;
      }
  
      menu.style.top = `${y}px`;
      menu.style.left = `${x}px`;
      menu.classList.remove('hidden');
  }
  
  private _populateContextMenu(isCollapsed: boolean, hasSelectionText: boolean): void {
      const menuContainer = this.dom.customContextMenu;
      menuContainer.innerHTML = ''; // Clear previous items
      const ul = document.createElement('ul');
  
      const createMenuItem = (label: string, icon: string, action: string, disabled = false): HTMLLIElement => {
          const item = document.createElement('li');
          item.className = 'context-menu-item';
          if (disabled) item.classList.add('disabled');
          item.dataset.action = action;
          item.innerHTML = `
              <i class="fas fa-${icon}"></i>
              <span>${label}</span>
          `;
          item.addEventListener('mousedown', (e) => {
              e.preventDefault();
              if (!disabled) this._handleContextMenuAction(action);
          });
          return item;
      };
      
      const createSeparator = (): HTMLLIElement => {
          const separator = document.createElement('li');
          separator.className = 'context-menu-separator';
          return separator;
      }

      ul.appendChild(createMenuItem('Cut', 'cut', 'cut', !hasSelectionText));
      ul.appendChild(createMenuItem('Copy', 'copy', 'copy', !hasSelectionText));
      ul.appendChild(createMenuItem('Paste', 'paste', 'paste'));
      ul.appendChild(createSeparator());
  
      if (isCollapsed) {
          ul.appendChild(createMenuItem('Insert from Voice Note', 'microphone-alt', 'insert-voice'));
          ul.appendChild(createMenuItem('Select All', 'object-group', 'select-all'));
      } else {
          ul.appendChild(createMenuItem('Add Link', 'link', 'add-link', !hasSelectionText));
          ul.appendChild(createMenuItem('Polish Selection', 'wand-magic-sparkles', 'polish-selection', !hasSelectionText));
          ul.appendChild(createMenuItem('Rewrite Selection', 'pen-fancy', 'rewrite-selection', !hasSelectionText));
      }

      ul.appendChild(createSeparator());
      ul.appendChild(createMenuItem('Show Default Menu', 'ellipsis-v', 'show-default-menu'));
      menuContainer.appendChild(ul);
  }
  
  private _handleContextMenuAction(action: string): void {
      this.dom.customContextMenu.classList.add('hidden'); // Hide on action
      switch (action) {
          case 'insert-voice':
              this.audioService.startRecordingAtCursor();
              break;
          case 'select-all':
              document.execCommand('selectAll', false);
              break;
          case 'copy':
              document.execCommand('copy', false);
              break;
          case 'cut':
              document.execCommand('cut', false);
              break;
          case 'paste':
              document.execCommand('paste');
              break;
          case 'add-link':
              this.editorService.addLinkToSelection();
              break;
          case 'polish-selection':
              this.dialogService.handlePolishSelection();
              break;
          case 'rewrite-selection':
              this.dialogService.handleRewriteSelection();
              break;
          case 'show-default-menu':
              if (this.lastContextMenuEvent) {
                this.bypassContextMenu = true;
                const target = this.lastContextMenuEvent.target as HTMLElement;
                target.dispatchEvent(new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    clientX: this.lastContextMenuEvent.clientX,
                    clientY: this.lastContextMenuEvent.clientY
                }));
              }
              break;
      }
  }
  
  public updateSideBySideButtonState(): void {
    const note = this.noteService.currentNote;
    if (!note) {
        this.dom.sideBySideButton.disabled = true;
        return;
    }
    const hasRaw = !!note.rawTranscription.trim();
    const hasPolished = !!note.polishedNote.trim();
    const hasTranslated = !!note.translatedNote.trim();
    const canCompare = (hasRaw && hasPolished) || (hasPolished && hasTranslated);
    this.dom.sideBySideButton.disabled = !canCompare;
  }

  private toggleSideBySideView(): void {
      this.isSideBySideViewActive = !this.isSideBySideViewActive;
      document.body.classList.toggle('sbs-active', this.isSideBySideViewActive);
      this.dom.noteArea.classList.toggle('side-by-side-active', this.isSideBySideViewActive);
      this.dom.sideBySideButton.classList.toggle('active', this.isSideBySideViewActive);

      const containers = [this.dom.polishedContainer, this.dom.rawContainer, this.dom.translatedContainer];
      containers.forEach(c => c.classList.remove('sbs-visible'));

      if (this.isSideBySideViewActive) {
          this.activeTabBeforeSbs = document.querySelector('.tab-button.active') as HTMLButtonElement;
          containers.forEach(c => c.classList.remove('visible'));
          
          const note = this.noteService.currentNote;
          if (!note) return;

          const hasTranslated = !!note.translatedNote.trim();
          const currentTab = this.activeTabBeforeSbs.dataset.tab;

          if (currentTab === 'translated' && hasTranslated) {
              this.dom.polishedContainer.classList.add('sbs-visible');
              this.dom.translatedContainer.classList.add('sbs-visible');
          } else {
              this.dom.rawContainer.classList.add('sbs-visible');
              this.dom.polishedContainer.classList.add('sbs-visible');
          }
      } else {
          if (this.activeTabBeforeSbs) {
              this.dom.setActiveTab(this.activeTabBeforeSbs);
          } else {
              const polishedTab = this.dom.tabButtons[0] as HTMLButtonElement;
              this.dom.setActiveTab(polishedTab);
          }
      }
  }

  public async toggleStatsModal(): Promise<void> {
    const isHidden = this.dom.statsOverlay.classList.contains('hidden');
    this.dom.statsOverlay.classList.toggle('hidden', !isHidden);

    if (isHidden) { // If it was hidden, now it's visible, so calculate stats
        this.dom.wordCountDisplay.textContent = '...';
        this.dom.charCountDisplay.textContent = '...';
        this.dom.tokenCountDisplay.textContent = '...';

        const activeEditor = this.editorService.getActiveEditor();
        if (!activeEditor || activeEditor.classList.contains('placeholder-active')) {
            this.dom.wordCountDisplay.textContent = '0';
            this.dom.charCountDisplay.textContent = '0';
            this.dom.tokenCountDisplay.textContent = '0';
            return;
        }

        const text = activeEditor.innerText;
        const words = text.match(/\b\w+\b/g) || [];
        this.dom.wordCountDisplay.textContent = words.length.toLocaleString();
        this.dom.charCountDisplay.textContent = text.length.toLocaleString();

        this.dom.tokenCountDisplay.textContent = 'Calculating...';
        try {
            const tokenCount = await this.geminiService.countTokens(text, this.settingsService.currentModel);
            this.dom.tokenCountDisplay.textContent = tokenCount.toLocaleString();
        } catch (error) {
            this.dom.tokenCountDisplay.textContent = 'Unavailable';
        }
    }
  }

  // --- Token Usage History ---

  public addTokenUsageRecord(operation: string, usage: UsageMetadata | undefined, model: string, noteId: string | null): void {
    if (!usage) return;
    const record: Omit<TokenUsageRecord, 'id'> = {
      operation,
      usage,
      model,
      noteId,
      timestamp: Date.now(),
    };
    this.dbService.logTokenUsage(record);
  }

  private async renderTokenUsageTable(records: TokenUsageRecord[]): Promise<void> {
    const tableBody = this.dom.tokenUsageTableBody;
    tableBody.innerHTML = '';

    if (records.length === 0) {
        const row = tableBody.insertRow();
        row.className = 'token-usage-empty-row';
        const cell = row.insertCell();
        cell.colSpan = 7;
        cell.textContent = 'No AI operations have been logged yet.';
        return;
    }

    // Create a map of noteId -> noteTitle for quick lookup
    const noteIds = [...new Set(records.map(r => r.noteId).filter(Boolean))];
    const notes = await Promise.all(noteIds.map(id => this.dbService.getNote(id!)));
    const noteTitleMap = new Map(notes.filter(Boolean).map(n => [n!.id, n!.title]));

    for (const record of records) {
        const row = tableBody.insertRow();
        row.insertCell().textContent = new Date(record.timestamp).toLocaleString();
        row.insertCell().textContent = record.noteId ? noteTitleMap.get(record.noteId) ?? 'Note deleted' : 'N/A';
        row.insertCell().textContent = record.operation;
        row.insertCell().textContent = record.model;
        row.insertCell().textContent = record.usage.promptTokenCount.toLocaleString();
        row.insertCell().textContent = record.usage.candidatesTokenCount.toLocaleString();
        row.insertCell().textContent = record.usage.totalTokenCount.toLocaleString();
    }
  }

  public async toggleTokenUsageModal(): Promise<void> {
    const isHidden = this.dom.tokenUsageOverlay.classList.contains('hidden');
    this.dom.tokenUsageOverlay.classList.toggle('hidden', !isHidden);
    if (isHidden) {
        const records = await this.dbService.getAllTokenUsageRecords();
        await this.renderTokenUsageTable(records);
    }
  }

  public updateConfirmSaveButtonState(): void {
    const isAnySelected = this.dom.savePolishedToggle.checked || this.dom.saveRawToggle.checked || this.dom.saveTranslatedToggle.checked;
    this.dom.confirmSaveButton.disabled = !isAnySelected;
  }

  private async exportTokenUsageToCSV(): Promise<void> {
    const records = await this.dbService.getAllTokenUsageRecords();
    if (records.length === 0) {
        this.statusManager.error('No token usage data to export');
        return;
    }

    const noteIds = [...new Set(records.map(r => r.noteId).filter(Boolean))];
    const notes = await Promise.all(noteIds.map(id => this.dbService.getNote(id!)));
    const noteTitleMap = new Map(notes.filter(Boolean).map(n => [n!.id, n!.title]));

    const headers = ['Timestamp', 'Note Title', 'Operation', 'Model', 'Prompt Tokens', 'Output Tokens', 'Total Tokens'];
    const rows = records.map(r => [
        new Date(r.timestamp).toISOString(),
        `"${(r.noteId ? noteTitleMap.get(r.noteId) ?? 'Note deleted' : 'N/A').replace(/"/g, '""')}"`,
        r.operation,
        r.model,
        r.usage.promptTokenCount,
        r.usage.candidatesTokenCount,
        r.usage.totalTokenCount
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        const date = new Date().toISOString().slice(0, 10);
        link.setAttribute('href', url);
        link.setAttribute('download', `confabulator_token_usage_${date}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
  }

  private setInitialCommandBarState(): void {
    const isCollapsed = localStorage.getItem('commandBarCollapsed') === 'true';
    if (isCollapsed) {
        this.dom.actionInterface.classList.add('collapsed');
    }
  }

  public toggleCommandBar(): void {
      const isCollapsed = this.dom.actionInterface.classList.toggle('collapsed');
      localStorage.setItem('commandBarCollapsed', String(isCollapsed));
  }

  public toggleFocusMode(): void {
    this.isFocusModeActive = !this.isFocusModeActive;
    document.body.classList.toggle('focus-mode-active', this.isFocusModeActive);
    
    // Update focus mode button in header
    const focusIcon = this.dom.focusModeButton.querySelector('i');
    if (focusIcon) {
        focusIcon.className = this.isFocusModeActive ? 'fas fa-compress' : 'fas fa-expand';
    }
    this.dom.focusModeButton.title = this.isFocusModeActive ? 'Exit Focus Mode' : 'Focus Mode';
    
    // Show/hide exit focus mode button
    if (this.isFocusModeActive) {
      this.dom.exitFocusModeButton.classList.remove('hidden');
    } else {
      this.dom.exitFocusModeButton.classList.add('hidden');
    }
  }
}